"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LaneStatesUploadRequest = exports.LaneStateEventRecord = exports.DegradedExitReason = exports.LaneState = void 0;
// Canonical securitydb 3f3aa15, SSC-155; scope comes from signed BOS assertion.
const zod_1 = require("zod");
const IsoDateTime = zod_1.z.string().datetime();
/**
 * The lane-state upload (design §6, §10; SSC-155): one record per change of
 * a lane's state, sent by the lane through `POST /sync/lane-states` as
 * `{ records: [record] }`.
 *
 * A lane state is not a weighing. Entering and leaving `fault` or
 * `degraded` happen whether or not anything is weighed, so they are their
 * own upload and their own table (`lane_state_event`), not fields on the
 * weight sample. With them, the time a lane spent in `degraded` is exact;
 * without them it can only be guessed from the weighings that carried
 * `laneState = degraded`.
 *
 * - The tenant, the store and the register are never in the body. BOS
 *   takes the terminal from its authentication and puts it in the
 *   assertion, as for weighings.
 * - `clientUuid` is generated on the lane and is the idempotency key, with
 *   the register.
 * - `attendantId` is the BOS id of the attendant who acted, if one did.
 *   SecureScale stores only its pseudonym (§11).
 * - Unknown extra fields are tolerated and dropped (zod's default), so a
 *   record from a newer lane still parses.
 */
/**
 * A lane's state (design §6). `none` is configuration, not an event: a
 * lane without a scale sends nothing.
 */
exports.LaneState = zod_1.z.enum(['healthy', 'fault', 'degraded']);
/** Why a lane left `degraded`. */
exports.DegradedExitReason = zod_1.z.enum([
    /** `degraded_max` passed without a renewal. */
    'expired',
    /** The scale showed it works again. */
    'scale_recovered',
    /** An attendant ended it early. */
    'ended',
]);
/**
 * One change of lane state.
 *
 * - `state` is the state entered. A renewal of `degraded` is sent as
 *   `degraded` → `degraded` with the new `expiresAt`.
 * - `previousState` is the state left. It is null or absent on the first
 *   record after the lane starts, which says what state it started in; a
 *   lane that restarts while `degraded` is no longer degraded, and this
 *   record is what closes the interval.
 * - `expiresAt` is set while `degraded`: the end of `degraded_max`. A
 *   degraded interval never runs past it without a renewal.
 */
exports.LaneStateEventRecord = zod_1.z
    .object({
    schemaVersion: zod_1.z.literal(1).optional(),
    clientUuid: zod_1.z.string().min(1).max(64),
    state: exports.LaneState,
    previousState: exports.LaneState.nullable().optional(),
    /** Lane clock, UTC. */
    occurredAt: IsoDateTime,
    /** The attendant's reason for `degraded` (design §6). */
    reason: zod_1.z.string().trim().min(1).max(256).optional(),
    /** The scale fault, for `fault`, or the fault `degraded` was entered from. */
    faultCode: zod_1.z.string().min(1).max(64).optional(),
    /** Set when leaving `degraded`. */
    exitReason: exports.DegradedExitReason.optional(),
    /** While `degraded`: when it expires unless renewed. */
    expiresAt: IsoDateTime.optional(),
    /** Who acted, if anyone: the BOS id, stored only as a pseudonym (§11). */
    attendantId: zod_1.z.string().trim().min(1).max(64).nullable().optional(),
    /** Scale serial number. */
    scaleId: zod_1.z.string().min(1).max(64).optional(),
})
    .superRefine((r, ctx) => {
    if (r.state === 'degraded') {
        if (!r.attendantId || !r.reason)
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: 'Degraded activation requires attendant and reason',
            });
        if (!r.expiresAt || new Date(r.expiresAt) <= new Date(r.occurredAt))
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['expiresAt'],
                message: 'Degraded expiry must be after activation',
            });
    }
});
exports.LaneStatesUploadRequest = zod_1.z.object({
    records: zod_1.z.array(exports.LaneStateEventRecord).min(1).max(5000),
});
//# sourceMappingURL=lane-state.js.map